import {
  useMutation,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import type { z } from "zod";

import type { GoalStatus } from "~/../drizzle/schema";
import { decryptResourceFields } from "~/lib/crypto";
import type { KeyManager } from "~/lib/crypto";
import {
  addMilestoneOfLifeFn,
  deleteMilestoneOfLifeFn,
  getMilestonesByGoalIdFn,
  getMilestonesByGoalIdForCoachFn,
  moveMilestoneOfLifeFn,
  renameMilestoneOfLifeFn,
  syncMilestonesOfLifeFn,
  updateMilestoneStatusFn,
} from "~/lib/functions/milestone-of-life";
import type {
  AddMilestoneOfLife,
  RenameMilestoneOfLife,
  SyncMilestonesOfLife,
} from "~/lib/schemas/milestone-of-life";

import { goalOfLifeQueryKeys } from "./goalOfLifeQuery";

export const milestoneQueryKeys = {
  all: ["milestones"] as const,
  byGoal: (goalId: string) =>
    [...milestoneQueryKeys.all, "byGoal", goalId] as const,
};

type MilestoneRow = Awaited<ReturnType<typeof getMilestonesByGoalIdFn>>[number];

/** A milestone with its title decrypted for the viewer. */
export type DecryptedMilestone = {
  id: string;
  title: string;
  status: GoalStatus;
  order: number;
  /** False for a step written before titles were encrypted, until the owner's browser encrypts it. */
  isEncrypted: boolean;
  /** False when the title could not be decrypted and shows a placeholder. */
  isReadable: boolean;
};

async function decryptMilestones(
  steps: MilestoneRow[],
  keyManager: KeyManager | null,
): Promise<DecryptedMilestone[]> {
  return Promise.all(
    steps.map(async (step) => {
      const { title, encryptedTitle } = step;
      let decrypted = title;
      if (encryptedTitle && keyManager) {
        try {
          ({ title: decrypted } = await decryptResourceFields({
            resourceType: "goal",
            resourceId: step.id,
            keyManager,
            fields: { title: { encrypted: encryptedTitle, plaintext: title } },
          }));
        } catch (error) {
          console.warn("Milestone title could not be decrypted", {
            milestoneId: step.id,
            error,
          });
        }
      }
      return {
        id: step.id,
        title: decrypted ?? "Untitled",
        status: step.status,
        order: step.order,
        isEncrypted: !!encryptedTitle,
        isReadable: decrypted !== null,
      };
    }),
  );
}

/**
 * A goal's milestones with decrypted titles. The owner reads their own; a
 * coach reads a client's shared goal through the coach read.
 */
export const milestonesByGoalQueryOptions = ({
  goalId,
  viewer,
  keyManager,
}: {
  goalId: string;
  viewer: "owner" | "coach";
  keyManager: KeyManager | null;
}) =>
  queryOptions({
    queryKey: [
      ...milestoneQueryKeys.byGoal(goalId),
      viewer,
      keyManager ? "unlocked" : "locked",
    ],
    queryFn: async () => {
      const steps =
        viewer === "coach"
          ? await getMilestonesByGoalIdForCoachFn({ data: { goalId } })
          : await getMilestonesByGoalIdFn({ data: { goalId } });
      return decryptMilestones(steps, keyManager);
    },
    enabled: !!goalId,
  });

export const useUpdateMilestoneStatus = () => {
  const onSuccess = useInvalidateSteps();
  return useMutation({
    mutationFn: (data: { id: string; status: GoalStatus }) =>
      updateMilestoneStatusFn({ data }),
    onSuccess,
  });
};

export const useMoveMilestone = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { id: string; newOrder: number }) =>
      moveMilestoneOfLifeFn({ data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: milestoneQueryKeys.all,
      });
    },
  });
};

/** Refreshes a goal's steps and the goal list, whose counts and states follow them. */
function useInvalidateSteps() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: milestoneQueryKeys.all }),
      queryClient.invalidateQueries({ queryKey: goalOfLifeQueryKeys.all }),
    ]);
}

export const useSyncMilestones = () => {
  const onSuccess = useInvalidateSteps();
  return useMutation({
    mutationFn: (data: z.infer<typeof SyncMilestonesOfLife>) =>
      syncMilestonesOfLifeFn({ data }),
    onSuccess,
  });
};

export const useAddMilestone = () => {
  const onSuccess = useInvalidateSteps();
  return useMutation({
    mutationFn: (data: z.infer<typeof AddMilestoneOfLife>) =>
      addMilestoneOfLifeFn({ data }),
    onSuccess,
  });
};

export const useRenameMilestone = () => {
  const onSuccess = useInvalidateSteps();
  return useMutation({
    mutationFn: (data: z.infer<typeof RenameMilestoneOfLife>) =>
      renameMilestoneOfLifeFn({ data }),
    onSuccess,
  });
};

export const useDeleteMilestone = () => {
  const onSuccess = useInvalidateSteps();
  return useMutation({
    mutationFn: (id: string) => deleteMilestoneOfLifeFn({ data: { id } }),
    onSuccess,
  });
};
